import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
	Box,
	Flex,
	Text,
	Container,
	Heading,
	VStack,
	HStack,
} from '@chakra-ui/react';

const SECTIONS = [
	{ id: 'info-collect', title: '1. Information We Collect' },
	{ id: 'how-use', title: '2. How We Use Your Information' },
	{ id: 'sharing', title: '3. Information Sharing & Disclosure' },
	{ id: 'data-security', title: '4. Data Security' },
	{ id: 'cookies', title: '5. Cookies & Tracking' },
	{ id: 'retention', title: '6. Data Retention' },
	{ id: 'your-rights', title: '7. Your Rights' },
	{ id: 'children', title: '8. Children\'s Privacy' },
	{ id: 'changes', title: '9. Changes to This Policy' },
	{ id: 'contact', title: '10. Contact Us' },
];

const SectionHeading = ({ id, children }) => (
	<Heading
		id={id}
		as="h2"
		fontSize={{ base: '2xl', md: '28px' }}
		fontFamily="'Poppins', sans-serif"
		fontWeight="700"
		color="#111113"
		mt={{ base: 10, md: 14 }}
		mb={{ base: 3, md: 4 }}
		scrollMarginTop="100px"
	>
		{children}
	</Heading>
);

const Para = ({ children, ...props }) => (
	<Text
		fontSize={{ base: 'md', md: 'lg' }}
		color="#4B5563"
		lineHeight="1.75"
		mb={4}
		{...props}
	>
		{children}
	</Text>
);

const BulletList = ({ items }) => (
	<Box as="ul" pl={6} mb={4}>
		{items.map((item, i) => (
			<Box as="li" key={i} mb={2}>
				<Text fontSize={{ base: 'md', md: 'lg' }} color="#4B5563" lineHeight="1.75">
					{item}
				</Text>
			</Box>
		))}
	</Box>
);

const PrivacyPage = () => {
	return (
		<Box w="100%" minH="100vh" bg="white" fontFamily="'DM Sans', sans-serif">
			{/* Header */}
			<Box bg="#F2F7F8" pt={{ base: '100px', md: '120px' }} pb={{ base: 12, md: 16 }}>
				<Container maxW="container.lg">
					<VStack spacing={4} textAlign="center">
						<Heading
							as="h1"
							fontSize={{ base: '4xl', md: '52px' }}
							fontFamily="'Poppins', sans-serif"
							fontWeight="700"
							color="#111113"
							lineHeight="1.15"
						>
							Privacy Policy
						</Heading>
						<Text fontSize={{ base: 'md', md: 'xl' }} color="#6B7280" maxW="2xl">
							We collect what we need to run your event — nothing more. This policy describes exactly what that is and how we handle it.
						</Text>
						<Text fontSize="sm" color="#9CA3AF" mt={2}>
							Last updated: October 9, 2026
						</Text>
					</VStack>
				</Container>
			</Box>

			{/* Content */}
			<Container maxW="container.lg" py={{ base: 10, md: 16 }}>
				<Flex direction={{ base: 'column', lg: 'row' }} gap={{ base: 8, lg: 16 }}>
					{/* Sidebar TOC — sticky on desktop */}
					<Box
						as="nav"
						w={{ base: '100%', lg: '280px' }}
					flexShrink={0}
					position={{ base: 'static', lg: 'sticky' }}
					top="100px"
					alignSelf="flex-start"
				>
						<Box
							bg="#F9FBFA"
							borderRadius="2xl"
							p={6}
							border="1px solid"
							borderColor="rgba(217, 219, 233, 0.6)"
						>
							<Text fontSize="sm" fontWeight="700" color="#111113" mb={4} textTransform="uppercase" letterSpacing="0.05em">
								Contents
							</Text>
							<VStack align="flex-start" spacing={2}>
								{SECTIONS.map((s) => (
									<Text
										key={s.id}
										as="a"
										href={`#${s.id}`}
										fontSize="sm"
										color="#6B7280"
										_hover={{ color: '#FB8601' }}
										transition="color 0.15s"
										cursor="pointer"
									>
										{s.title}
									</Text>
								))}
							</VStack>
						</Box>
					</Box>

					{/* Main content */}
					<Box flex={1} minW={0}>
						<Para>
							Stratospia Limited (RC 9526323), a company registered in Nigeria ("Stratospia", "we", "our", or "us"), operates the Eventory platform, including our website and mobile applications. This Privacy Policy explains what information we collect, how we use it, and the choices you have regarding your data.
						</Para>

						<SectionHeading id="info-collect">1. Information We Collect</SectionHeading>
						<Text fontSize={{ base: 'lg', md: 'xl' }} fontWeight="600" color="#111113" mb={3}>
							Information you provide
						</Text>
						<BulletList items={[
							'Account details — name, email address, and password when you register as a caterer or staff member.',
							'Event data — event names, dates, venues, menus, table layouts, kitchen station configurations, and team-member assignments you create on the platform.',
							'Payment information — billing details processed securely through Paystack, our payment processor. Eventory never sees or stores your card number; Paystack tokenises cards on our behalf.',
							'Guest details (optional) — when a guest opens a menu via QR they may type a first name to appear on the kitchen ticket, and an optional phone number to receive an SMS receipt for any paid items. We do not require either; orders work without them.',
							'Communications — messages you send through the platform or to our support team.',
						]} />
						<Text fontSize={{ base: 'lg', md: 'xl' }} fontWeight="600" color="#111113" mb={3}>
							Information collected automatically
						</Text>
						<BulletList items={[
							'Device and browser information (type, operating system, screen resolution).',
							'IP address and approximate geographic location.',
							'Usage data — pages visited, features used, clicks, time spent, and interaction patterns.',
							'Cookies and similar tracking technologies (see Section 5).',
						]} />

						<SectionHeading id="how-use">2. How We Use Your Information</SectionHeading>
						<Para>We use the information we collect to:</Para>
						<BulletList items={[
							'Provide, maintain, and improve the Eventory platform and its features.',
							'Process transactions and send related information (confirmations, invoices).',
							'Send service-related notices, security alerts, and support messages.',
							'Personalize your experience and deliver content relevant to your interests.',
							'Monitor and analyze usage trends to improve our products and services.',
							'Detect, investigate, and prevent fraudulent or unauthorized activity.',
							'Comply with legal obligations and enforce our Terms of Service.',
						]} />

						<SectionHeading id="sharing">3. Information Sharing & Disclosure</SectionHeading>
						<Para>
							We do not sell your personal information. We may share data in the following circumstances:
						</Para>
						<BulletList items={[
							'Service providers — Heroku (application hosting), MongoDB Atlas (database), Paystack (payment processing), Termii (SMS delivery for guest receipts), and analytics providers. Each operates under data-protection agreements; we share only what they need to deliver their service.',
							'Event collaborators — other Eventory users you invite to co-manage an event will have access to that event\u2019s data.',
							'Legal requirements — if required by law, subpoena, or government request, or to protect our rights, property, or safety.',
							'Business transfers — in connection with a merger, acquisition, or sale of assets, your information may be transferred to the acquiring entity.',
						]} />

						<SectionHeading id="data-security">4. Data Security</SectionHeading>
						<Para>
							We implement industry-standard security measures to protect your data, including encrypted connections (TLS/SSL), hashed passwords, role-based access controls, and regular security audits. While no method of transmission or storage is 100% secure, we are committed to safeguarding your information to the best of our ability.
						</Para>

						<SectionHeading id="cookies">5. Cookies & Tracking</SectionHeading>
						<Para>
							Eventory uses cookies and similar technologies to maintain session state, remember your preferences, and understand how you use the platform. You can manage cookie preferences through your browser settings. Disabling cookies may limit certain features of the service.
						</Para>
						<BulletList items={[
							'Essential cookies — required for the platform to function (authentication, security).',
							'Analytics cookies — help us understand usage patterns and improve the product.',
							'Preference cookies — remember your settings and customization choices.',
						]} />

						<SectionHeading id="retention">6. Data Retention</SectionHeading>
						<Para>
							We retain your personal information for as long as your account is active or as needed to provide you with our services. If you delete your account, we will remove your personal data within 30 days, except where retention is required by law or for legitimate business purposes (such as fraud prevention or dispute resolution).
						</Para>

						<SectionHeading id="your-rights">7. Your Rights</SectionHeading>
						<Para>Depending on your location, you may have the right to:</Para>
						<BulletList items={[
							'Access the personal data we hold about you.',
							'Correct inaccurate or incomplete information.',
							'Request deletion of your personal data.',
							'Object to or restrict certain types of processing.',
							'Request a portable copy of your data.',
							'Withdraw consent at any time where processing is based on consent.',
						]} />
						<Para>
							To exercise any of these rights, please contact us at <strong>jason@geteventory.app</strong>. We will respond to your request within 30 days.
						</Para>

						<SectionHeading id="children">8. Children's Privacy</SectionHeading>
						<Para>
							Eventory is not directed at children under the age of 16. We do not knowingly collect personal information from children. If you believe a child has provided us with personal data, please contact us and we will promptly delete it.
						</Para>

						<SectionHeading id="changes">9. Changes to This Policy</SectionHeading>
						<Para>
							We may update this Privacy Policy from time to time. When we make material changes, we will notify you by email or by posting a prominent notice on our platform. Your continued use of Eventory after such changes constitutes acceptance of the updated policy.
						</Para>

						<SectionHeading id="contact">10. Contact Us</SectionHeading>
						<Para>
							If you have questions or concerns about this Privacy Policy or our data practices, please contact us:
						</Para>
						<Box
							bg="#F9FBFA"
							borderRadius="2xl"
							p={6}
							border="1px solid"
							borderColor="rgba(217, 219, 233, 0.6)"
							mb={4}
						>
							<Text fontSize="md" color="#4B5563" lineHeight="1.75" mb={1}><strong>Email:</strong> jason@geteventory.app</Text>
							<Text fontSize="md" color="#4B5563" lineHeight="1.75"><strong>Address:</strong> Stratospia Limited, Lagos, Nigeria</Text>
						</Box>
					</Box>
				</Flex>
			</Container>

			{/* Footer */}
			<Box bg="#F2F7F8" py={8}>
				<Container maxW="container.xl">
					<Flex
						direction={{ base: 'column', md: 'row' }}
						justify="space-between"
						align="center"
						gap={4}
					>
						<HStack spacing={6}>
							<Text
								as={RouterLink}
								to="/privacy"
								fontSize="sm"
								fontWeight="600"
								color="#FB8601"
								_hover={{ textDecoration: 'underline' }}
							>
								Privacy Policy
							</Text>
							<Text
								as={RouterLink}
								to="/terms"
								fontSize="sm"
								color="#6B7280"
								_hover={{ color: '#FB8601' }}
							>
								Terms of Service
							</Text>
							<Text
								as={RouterLink}
								to="/"
								fontSize="sm"
								color="#6B7280"
								_hover={{ color: '#FB8601' }}
							>
								Home
							</Text>
						</HStack>
						<Text color="#9E9E9E" fontSize="sm" fontWeight="500">
							© 2026 Stratospia Limited. All rights reserved.
						</Text>
					</Flex>
				</Container>
			</Box>
		</Box>
	);
};

export default PrivacyPage;
